import { useEffect } from 'react';
import { addYears } from 'date-fns';
import SocketIO from 'socket.io-client';

import { DeviceType } from '../types';

import { getAreaAlerts, getLatestDeviationAlerts } from '../apis/AreaApi';
import { getDasairAlerts } from '../apis/DasAirApi';
import { getDascasAlerts } from '../apis/DascasApi';
import { getDasConcreteAlerts } from '../apis/DasconcreteApi';
import { getDaslockAlerts } from '../apis/DaslockApi';
import { getDaspowerAlerts } from '../apis/DaspowerApi';
import { getDastempAlerts } from '../apis/DastempApi';
import { getDaswaterLAlerts } from '../apis/DaswaterApi';
import { getDasWBGTAlerts } from '../apis/DasWbgtApi';
import {
  getAssetAlerts,
  getDasgasAlerts,
  getDasloopAlerts,
  getDasSenseHAlerts,
  getDaswatchAlerts,
  getHeatStrokeAlerts,
} from '../apis/ProjectApi';
import { getResourceAlerts } from '../apis/ResourceApi';

import { delay } from '../utils/common';
import PubSub from '../utils/PubSub';

type AlertType =
  | DeviceType
  | 'area'
  | 'deviation'
  | 'dascas_g'
  | 'asset'
  | 'ai'
  | 'heatstroke';

type AlertHandlerData = {
  id: string;
  projectId: string;
  timestamp: string;
};

const alertFetchers: Partial<
  Record<AlertType, (data: AlertHandlerData) => Promise<any>>
> = {
  dasloop: (data) => getDasloopAlerts(data.projectId, { id: data.id }),
  dastrack: (data) => {
    const websocketAlert: any = data;
    return Promise.resolve({
      data: {
        data: [
          {
            id: websocketAlert.id,
            dasId: websocketAlert.dasId,
            readAt: websocketAlert.readAt,
            dismissedAt: websocketAlert.dismissedAt,
            alertType: websocketAlert.alert.type,
            timestamp: websocketAlert.timestamp,
            plantId: websocketAlert?.plantId ?? '',
            device: websocketAlert?.alert?.value?.device ?? 0,
            distance: websocketAlert?.alert?.value?.distance ?? 0,
            tagId: websocketAlert.tagId,
            collision: websocketAlert?.collision,
          },
        ],
      },
    });
  },
  dasrp: (data) => {
    const websocketAlert: any = data;
    return Promise.resolve({
      data: {
        data: [
          {
            id: websocketAlert.id,
            dasId: websocketAlert.dasId,
            readAt: websocketAlert.readAt,
            dismissedAt: websocketAlert.dismissedAt,
            alertType: websocketAlert.alert.type,
            timestamp: websocketAlert.timestamp,
            plantId: websocketAlert?.plantId ?? '',
            device: websocketAlert?.alert?.value?.device ?? 0,
            distance: websocketAlert?.alert?.value?.distance ?? 0,
            tagId: websocketAlert.tagId,
            collision: websocketAlert?.collision,
          },
        ],
      },
    });
  },
  dasgas: (data) => getDasgasAlerts(data.projectId, { id: data.id }),
  daspower: (data) => getDaspowerAlerts(data.projectId, { id: data.id }),
  dastemp: (data) => getDastempAlerts(data.projectId, { id: data.id }),
  dasair: (data) => getDasairAlerts(data.projectId, { id: data.id }),
  daswater: (data) => getDaswaterLAlerts(data.projectId, { id: data.id }),
  daswatch: (data) => getDaswatchAlerts(data.projectId, { id: data.id }),
  area: (data) => getAreaAlerts(data.projectId, { id: data.id }),
  deviation: (data) =>
    getLatestDeviationAlerts(data.projectId, {
      vehicleId: (data as any).deviation.vehicleId,
      fromDate: addYears(new Date(), -1).toISOString(),
      toDate: new Date().toISOString(),
      order: 'desc',
    }),
  daslock: (data) => getDaslockAlerts(data.projectId, { id: data.id }),
  dascas_g: (data) => getDascasAlerts(data.projectId, { id: data.id }),
  dasconcrete: (data) => getDasConcreteAlerts(data.projectId, { id: data.id }),
  asset: (data) => getAssetAlerts(data.projectId, { id: data.id }),
  ai: (data) =>
    getResourceAlerts({
      projectId: data.projectId,
      resourceType: 'ai',
      params: {
        id: data.id,
        limit: 1,
      },
    }),
  dassenses: (data) => getDasSenseHAlerts(data.projectId, { id: data.id }),
  daswbgt: (data) => getDasWBGTAlerts(data.projectId, { id: data.id }),
  heatstroke: (data) => getHeatStrokeAlerts(data.projectId, { id: data.id }),
};

export const alertHandler = async (
  currentProjectId,
  type: AlertType,
  data: AlertHandlerData,
) => {
  if (currentProjectId !== data.projectId) {
    return null;
  }

  const fetchAlert = alertFetchers[type];
  let res;
  let count = 1;

  // Some alert types are created first and get their `alert` details filled
  // in later; treat a fetched item as not-yet-ready until `alert` is set.
  const isIncomplete = (item: any) => !item || ('alert' in item && !item.alert);

  do {
    console.info(`alert ${data.id} query count: ${count}`);

    try {
      res = await fetchAlert?.(data);
    } catch (e) {
      console.error('Error fetching alert data:', e);
    }
    count++;
    await delay(2000);
  } while (
    count <= 10 &&
    (res?.data?.data?.length !== 1 || isIncomplete(res.data.data[0]))
  );

  if (res?.data.data.length === 1) {
    return {
      type: type,
      data: res.data.data[0],
      pipes: res.data?.pipes,
    };
  }

  return null;
};

const useWebsocket = (accessToken: string | null, pubsub: PubSub | null) => {
  useEffect(() => {
    if (accessToken && pubsub) {
      const socket = SocketIO(import.meta.env.VITE_WEBSOCKET_URL ?? '', {
        extraHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      socket.connect();

      socket.onAny((eventName, data) => {
        switch (eventName) {
          case 'dasloop-protocol-alert':
          case 'das-endpoint-alert':
            pubsub.publish('alert', { ...data, type: data.endpointType });
            break;
          case 'deviation-alert':
            pubsub.publish('alert', { ...data, type: 'deviation' });
            break;
          case 'area-alert':
            pubsub.publish('alert', { ...data, type: 'area' });
            break;
          case 'smartlock-protocol-alert':
            pubsub.publish('alert', { ...data, type: 'daslock' });
            break;
          case 'resource-ai-alert':
            pubsub.publish('alert', { ...data, type: 'ai' });
            break;
          default:
            pubsub.publish(eventName, data);
        }
      });

      return () => {
        socket.disconnect();
      };
    }
  }, [accessToken, pubsub]);
};

export default useWebsocket;
